/**
 * formValidation.js is a validation config that works against validatejs
 *
 * docs: https://validatejs.org/#validators
 */
import { TYPES_MAP } from '../constants/ListingCriteriaConstants';

const constraints = {
  username: {
    format: {
      pattern: '[a-z0-9]+',
      flags: 'i',
      message: 'can only contain a-z and 0-9',
    },
  },
};

const postingConstraints = {
  // `floorPlanName` alias for `currentProperty.name`
  floorPlanName: {
    presence: {
      allowEmpty: false,
      message: 'cannot be empty.',
    },
    format: {
      pattern: /^[-A-Za-z0-9 ]+$/,
      message: `can only contain alphanumeric and ‘-’.`,
    },
  },
  // `unitName` alias for 'currentProperty.floorplans[i].name'
  unitName: {
    presence: {
      allowEmpty: false,
      message: 'cannot be empty.',
    },
    format: {
      pattern: /^[-A-Za-z0-9 ]+$/,
      message: `can only contain alphanumeric and ‘-’.`,
    },
  },
  beds: {
    presence: {
      allowEmpty: false,
      message: 'Beds cannot be blank.',
    },
  },
  baths: {
    presence: {
      allowEmpty: false,
      message: 'Bathrooms cannot be blank.',
    },
  },
  contactEmail: {
    presence: {
      allowEmpty: false,
      message: 'Email cannot be blank.',
    },
    email: { message: 'Please provide a valid email address' },
  },
  contactName: {
    presence: {
      allowEmpty: false,
      message: 'Name cannot be blank.',
    },
    format: {
      pattern: /^[^0-9^@]+$/,
      message: `Name cannot include numbers or @.`,
    },
  },
  roomDescription: {
    presence: {
      allowEmpty: false,
      message: 'Description cannot be blank',
    },
  },
  forRentBy: {
    presence: {
      allowEmpty: false,
      message: 'Listed by cannot be blank.',
    },
  },
  leaseDuration: {
    presence: {
      allowEmpty: false,
      message: 'Lease duration cannot be blank.',
    },
  },
  price: {
    presence: {
      allowEmpty: false,
      message: 'Price cannot be blank',
    },
    numericality: {
      onlyInteger: true,
      greaterThanOrEqualTo: 100,
      lessThanOrEqualTo: 1000000,
      notInteger: 'Enter only dollar amount, without cents',
      notValid: 'Price must be a number',
      notGreaterThanOrEqualTo: 'Price must be between $100 and $1,000,000',
      notLessThanOrEqualTo: 'Price must be between $100 and $1,000,000',
    },
  },
  roommateMinAgePreference: {
    presence: {
      allowEmpty: true,
      message: 'Age cannot be blank.',
    },
    /**
     * type validation for null values does not work when numericality is present
     * handling this in the useForm hook by checking presence.allowEmpty
     */
    type: {
      type: (value: string) => {
        if (value === '') {
          // field can be null; return number
          return true;
        }

        return Number(value);
      },
      message: 'Enter only whole numbers',
    },
    numericality: {
      onlyInteger: true,
      strict: true,
      greaterThanOrEqualTo: 18,
      lessThanOrEqualTo: 200,
      notInteger: 'Enter only whole numbers',
      notValid: 'Age must be a number.',
      notGreaterThanOrEqualTo: 'Minimum age must be at least 18',
      notLessThanOrEqualTo: 'Minimum age must be between 18 and 200.',
    },
  },
  squareFeet: {
    presence: false,
    type: {
      type: (value: string) => {
        if (value === '') {
          // Skip validation for empty strings
          return null;
        }
        return Number(value);
      },
      message: 'Enter only whole numbers',
    },
    numericality: function (value: string) {
      if (value === '') {
        // Skip validation for empty strings
        return null;
      }
      return {
        onlyInteger: true,
        strict: true,
        greaterThanOrEqualTo: 10,
        lessThanOrEqualTo: 100000,
        notInteger: 'Enter only whole numbers',
        notValid: 'Enter only whole numbers',
        notGreaterThanOrEqualTo: 'Square feet must be between 10 and 100,000.',
        notLessThanOrEqualTo: 'Square feet must be between 10 and 100,000.',
      };
    },
  },
  roomSquareFeet: {
    presence: false,
    type: {
      type: (value: string) => {
        if (value === '') {
          // field can be null; return number
          return 1;
        }

        return Number(value);
      },
      message: 'Enter only whole numbers',
    },
    numericality: {
      onlyInteger: true,
      strict: true,
      greaterThanOrEqualTo: 10,
      lessThanOrEqualTo: 100000,
      notInteger: 'Enter only whole numbers',
      notValid: 'Enter only whole numbers',
      notGreaterThanOrEqualTo: 'Square feet must be between 10 and 100,000.',
      notLessThanOrEqualTo: 'Square feet must be between 10 and 100,000.',
    },
  },
  securityDeposit: {
    presence: false,
    type: {
      type: (value: string) => {
        if (value === '' || value === '0') {
          // field can be null or '0'; return number
          return 1;
        }

        return Number(value);
      },
      message: 'Enter only whole numbers',
    },
    numericality: {
      onlyInteger: true,
      greaterThanOrEqualTo: 0,
      lessThanOrEqualTo: 100000,
      notInteger: 'Enter only dollar amount, without cents',
      notValid: 'Price must be a number.',
      notGreaterThanOrEqualTo: 'Security deposit must be between $0 and $100,000.',
      notLessThanOrEqualTo: 'Security deposit must be between $0 and $100,000.',
    },
  },
  contact: { email: { message: 'doesn’t look like a valid email' } },
  [TYPES_MAP.PET_POLICY_PET_DEPOSIT]: {
    numericality: {
      onlyInteger: true,
      strict: true,
      greaterThanOrEqualTo: 0,
      lessThanOrEqualTo: 100000,
      notInteger: 'Enter only dollar amount, without cents',
      notValid: 'Enter only dollar amount, without cents',
      notGreaterThanOrEqualTo: 'Pet deposit must be between $0 and $100,000',
      notLessThanOrEqualTo: 'Pet deposit must be between $0 and $100,000',
    },
  },
  [TYPES_MAP.PET_POLICY_PET_RENT]: {
    numericality: {
      onlyInteger: true,
      strict: true,
      greaterThanOrEqualTo: 0,
      lessThanOrEqualTo: 100000,
      notInteger: 'Enter only dollar amount, without cents',
      notValid: 'Enter only dollar amount, without cents',
      notGreaterThanOrEqualTo: 'Pet rent must be between $0 and $100,000',
      notLessThanOrEqualTo: 'Pet rent must be between $0 and $100,000',
    },
  },
  isInterestedInLearningMore: {
    presence: {
      allowEmpty: false,
      message: 'Selection must be made.',
    },
  },
};

const customConstraints = {
  isRoomSqrftLTESqrft: (roomSqrFt: number, sqrFt: number) => {
    const errorMessage = `‘Room square footage’ field can only be set if the value is less than or equal to the property’s square footage`;

    const response = roomSqrFt <= sqrFt;
    let message = null;

    if (response === false) {
      message = errorMessage;
    }

    return { response, message };
  },
  isSqrftGTERoomSqrft: (roomSqrFt: number, sqrFt: number) => {
    const errorMessage = `‘Square footage’ field can only be set if the value is more than or equal to the room’s square footage’`;

    const response = roomSqrFt <= sqrFt;
    let message = null;

    if (response === false) {
      message = errorMessage;
    }

    return { response, message };
  },
};

const getFirstErrorMessage = (array: Array<string>) => array[0];

/**
 * removes the error key in the message
 * camelCaseKeys become words in the message
 * contactName => Contact name
 *
 * ex:
 * "Sqft Enter only whole numbers"
 * returns "Enter only whole numbers"
 *
 * "Contact name Name cannot be blank"
 * return "Name cannot be blank"
 */
const removeErrorKey = (key: string, message: string) => {
  const words = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  const keyUpperFirst = words.charAt(0).toUpperCase() + words.slice(1);

  return message.replace(keyUpperFirst, '').trim();
};

const listingCriteriaConstraints = {
  [TYPES_MAP.PET_POLICY_PET_DEPOSIT]: {
    numericality: {
      onlyInteger: true,
      strict: true,
      greaterThanOrEqualTo: 0,
      lessThanOrEqualTo: 100000,
      notInteger: 'Enter only dollar amount, without cents',
      notValid: 'Enter only dollar amount, without cents',
      notGreaterThanOrEqualTo: 'Pet deposit must be between $0 and $100,000.',
      notLessThanOrEqualTo: 'Pet deposit must be between $0 and $100,000.',
    },
  },
  [TYPES_MAP.PET_POLICY_PET_RENT]: {
    numericality: {
      onlyInteger: true,
      strict: true,
      greaterThanOrEqualTo: 0,
      lessThanOrEqualTo: 100000,
      notInteger: 'Enter only dollar amount, without cents',
      notValid: 'Enter only dollar amount, without cents',
      notGreaterThanOrEqualTo: 'Pet rent must be between $0 and $100,000',
      notLessThanOrEqualTo: 'Pet rent must be between $0 and $100,000',
    },
  },
};

export {
  constraints,
  customConstraints,
  getFirstErrorMessage,
  listingCriteriaConstraints,
  postingConstraints,
  removeErrorKey,
};
